import { useEffect, useState, ReactNode } from 'react';
import { DropdownMenu } from '../../form';
import { Icon, Loading } from '../../UI';

export interface TableProps {
  className?: string;
  columns: string[];
  isSortable?: boolean;
  isLoading?: boolean;
  data?: ReactNode[][];
  emptyTableMessage?: string;
  textAlign?: 'left' | 'center' | 'right';

  actions?: string[];
  onActionClick?: (item: string, rowIndex: number) => void;
}

export const Table = ({
  columns,
  data,
  className,
  textAlign = 'left',
  emptyTableMessage,
  isSortable,
  isLoading,
  actions = [],
  onActionClick,
}: TableProps) => {
  const classNameProp = className || '';

  const [sortBy, setSortBy] = useState(0);
  const [sortAscending, setSortAscending] = useState(false);

  const [sortedData, setSortedData] = useState<string[][]>([]);

  useEffect(() => {
    const indexedData: ReactNode[][] = [];
    data?.forEach((row, index) => {
      indexedData.push([...row, index.toString()]);
    });

    const sortedData = sortRows([...indexedData]);
    setSortedData(sortedData);
  }, [data]);

  useEffect(() => {
    if (sortedData.length === 0) {
      return;
    }

    const updatedSortedData = sortRows([...sortedData]);
    setSortedData(updatedSortedData);
  }, [sortBy, sortAscending]);

  const sortRows = (rows) => {
    return rows.sort(rowSortFunction);
  };

  const rowSortFunction = (a, b) => {
    if (a[sortBy] === b[sortBy]) {
      return 0;
    } else {
      if (sortAscending) {
        return a[sortBy] > b[sortBy] ? -1 : 1;
      } else {
        return a[sortBy] < b[sortBy] ? -1 : 1;
      }
    }
  };

  const handleColumnClick = (columnIndex) => {
    if (isSortable) {
      if (sortBy !== columnIndex) {
        setSortBy(columnIndex);
        setSortAscending(false);
      } else if (sortBy === columnIndex) {
        setSortAscending(!sortAscending);
      }
    }
  };

  const handleActionsClick = (item, rowIndex) => {
    if (onActionClick) {
      onActionClick(item, rowIndex);
    }
  };

  return (
    <div className={`flex flex-col ${classNameProp} overflow-x-scroll`}>
      {/* Table headers */}
      <div className="flex justify-between border-b border-opacity-50 border-grey-30 dark:border-grey-70 select-none">
        {columns.map((column, columnIndex) => (
          <div
            key={`column_wrapper_${column}_${columnIndex}`}
            className={`flex items-center w-full uppercase font-semibold text-sm py-3
            ${isSortable ? 'cursor-pointer' : ''}
            ${
              textAlign === 'center'
                ? 'justify-center px-2'
                : textAlign === 'right'
                ? 'justify-end pr-5'
                : 'justify-start pl-5'
            }
            `}
            onClick={() => handleColumnClick(columnIndex)}
          >
            {isSortable && (
              <Icon
                color={columnIndex === sortBy ? 'black' : 'grey-30'}
                svgName={
                  sortAscending && columnIndex === sortBy
                    ? 'sortUp'
                    : 'sortDown'
                }
              />
            )}
            <span>{column}</span>
          </div>
        ))}
      </div>
      {/* Table body */}
      <div className="flex flex-col">
        {!isLoading && sortedData.length > 0 ? (
          <div className="flex flex-col border-b border-grey-10 dark:border-grey-70">
            {sortedData.map((row, index) => {
              const rowIndex = Number.parseInt(row[row.length - 1]);

              return (
                <div key={`row-wrapper-${rowIndex}`} data-row={index}>
                  <div
                    className={`flex w-full
                    ${
                      index < sortedData.length - 1
                        ? 'border-b border-grey-10'
                        : ''
                    }`}
                  >
                    {row
                      .filter(
                        (column, columnIndex) => columnIndex < row.length - 1
                      )
                      .map((column, columnIndex) => (
                        <div
                          key={`column-wrapper-${rowIndex}-${columnIndex}`}
                          data-col={columnIndex}
                          className={`flex items-center h-16 w-full text-sm`}
                        >
                          <span
                            className={`flex w-full border border-transparent
                          ${
                            textAlign === 'center'
                              ? 'justify-center px-2'
                              : textAlign === 'right'
                              ? 'justify-end pr-5'
                              : 'justify-start pl-5'
                          }
                          `}
                          >
                            {column}
                          </span>
                          {actions.length > 0 &&
                            columnIndex === row.length - 2 && (
                              <div className="relative flex items-center justify-center w-10 min-w-10 h-full">
                                <DropdownMenu
                                  dropdownTooltipClassName="bg-white border-solid border-grey-10 border shadow-md"
                                  dropdownItems={actions}
                                  handleClick={(item) =>
                                    handleActionsClick(item, rowIndex)
                                  }
                                  hideOnItemClick={true}
                                >
                                  <Icon
                                    svgName="options"
                                    className={'h-3 w-3'}
                                  />
                                </DropdownMenu>
                              </div>
                            )}
                        </div>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : isLoading ? (
          <Loading status={true} size={'small'} className={'mx-auto my-4'} />
        ) : (
          emptyTableMessage && (
            <div className="flex justify-between border-b border-opacity-50 border-grey-30 p-5 gap-3">
              <div className="flex justify-start w-full text-sm">
                <span>{emptyTableMessage}</span>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};
